import ContactLensSKU from '@/models/item/sku/contact-lens'
import { UsualPower } from '@/services/api/usual-power/usual-power'
import { Either } from 'fp-ts/lib/Either'

interface InitSkuStrategy {
  execute(ctx: InitSkuStrategyContext): InitSkuResult
}

interface InitSkuResult {
  selectedLeftSku: ContactLensSKU | null,
  selectedRightSku: ContactLensSKU | null,
}

interface InitSkuStrategyContext {
  skus: ContactLensSKU[]
}

const emptyInitResult: InitSkuResult = {
  selectedLeftSku: null,
  selectedRightSku: null,
}

class UsualPowerInitSkuStrategy implements InitSkuStrategy {
  constructor(
    private readonly usualPowerResult: Either<Error, UsualPower>
  ) {}

  execute(ctx: InitSkuStrategyContext): InitSkuResult {
    return this.usualPowerResult.fold(
      (error) => {
        throw error
      },
      (usualPower) => {
        const findSku = (userSettingsPower: string | undefined): ContactLensSKU | null => {
          if (!userSettingsPower) {
            return null
          }

          return ctx.skus.find((s) => {
            return s.powerTag && s.powerTag.pathname === userSettingsPower
          }) || null
        }

        return {
          selectedLeftSku: findSku(usualPower.leftPathname),
          selectedRightSku: findSku(usualPower.rightPathname),
        }
      }
    )
  }
}

class HeadInitSkuStrategy implements InitSkuStrategy {
  execute(ctx: InitSkuStrategyContext): InitSkuResult {
    const firstSku = ctx.skus.length > 0 ? ctx.skus[0] : null

    // GMC対策：購入可能または新作リクエスト受付中の場合は選択、それ以外は未選択
    // @see https://github.com/Everforth/growthpalette-front/issues/8884
    const selectedSku = firstSku && (!firstSku.isSoldOut || firstSku.cartInfo.newitem) ? firstSku : null

    return {
      selectedLeftSku: selectedSku,
      selectedRightSku: selectedSku,
    }
  }
}

class CompoundInitSkuStrategy {
  constructor(readonly strategies: InitSkuStrategy[]) {}

  execute(ctx: InitSkuStrategyContext): InitSkuResult {
    return this.strategies.reduce(
      (previousResult: InitSkuResult, strategy) => {
        const {
          selectedLeftSku: previousLeft,
          selectedRightSku: previousRight,
        } = previousResult

        const {
          selectedLeftSku: currentLeft,
          selectedRightSku: currentRight,
        } = strategy.execute(ctx)

        return {
          selectedLeftSku: previousLeft ? previousLeft : currentLeft,
          selectedRightSku: previousRight ? previousRight : currentRight,
        }
      },
      emptyInitResult
    )
  }
}

const createDefaultInitSkuStrategy = (eitherUsualPower: Either<Error, UsualPower>) => new CompoundInitSkuStrategy([
  new UsualPowerInitSkuStrategy(eitherUsualPower),
  new HeadInitSkuStrategy(),
])

export {
  UsualPowerInitSkuStrategy,
  HeadInitSkuStrategy,
  CompoundInitSkuStrategy,
  createDefaultInitSkuStrategy,
}
